import { z } from 'zod'
import { prioridadSchema, userRefSchema } from './primitives.js'

export const clientStateSchema = z.enum([
  'pendiente_implementacion',
  'implementacion',
  'qbr',
  'expansion',
  'retencion',
])
export type ClientState = z.infer<typeof clientStateSchema>

export const CLIENT_STATE_LABELS: Record<ClientState, string> = {
  pendiente_implementacion: 'Pendiente de implementación',
  implementacion: 'Implementación',
  qbr: 'QBR',
  expansion: 'Expansión',
  retencion: 'Retención',
}

/** Orden por riesgo, como lo pide el bloque de salud del portfolio. */
export const CLIENT_STATE_BY_RISK: readonly ClientState[] = [
  'pendiente_implementacion',
  'retencion',
  'implementacion',
  'expansion',
  'qbr',
]

export const segmentSchema = z.enum(['business', 'enterprise'])
export type Segment = z.infer<typeof segmentSchema>

export const SEGMENT_LABELS: Record<Segment, string> = {
  business: 'Business',
  enterprise: 'Enterprise',
}

export const activationSchema = z.object({
  contratados: z.number(),
  instalados: z.number(),
  brecha: z.number(),
  pctActivado: z.number(),
})
export type Activation = z.infer<typeof activationSchema>

/**
 * Ids del cliente en los sistemas de origen. Sin esto no hay deep link, y sin
 * deep link el panel obliga a buscar el registro a mano en el otro sistema.
 * null = no vinculado: la UI muestra "sin vincular" con la acción de vincular,
 * nunca un link roto.
 */
export const clientExternalIdsSchema = z.object({
  closeLeadId: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  plataformaClientId: z.string().nullable(),
})
export type ClientExternalIds = z.infer<typeof clientExternalIdsSchema>

export const EMPTY_CLIENT_EXTERNAL_IDS: ClientExternalIds = {
  closeLeadId: null,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  plataformaClientId: null,
}

/** Completa con null los ids que no se conocen. Para fixtures y adapters. */
export function clientExternalIds(
  partial: Partial<ClientExternalIds> = {},
): ClientExternalIds {
  return { ...EMPTY_CLIENT_EXTERNAL_IDS, ...partial }
}

export const clientSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  vertical: z.string(),
  estado: clientStateSchema,
  prioridadSeo: prioridadSchema,
  owner: userRefSchema.nullable(),
  segmento: segmentSchema,
  /** null cuando el rol no tiene permiso de ver $$. No es "sin dato". */
  mrr: z.number().nullable(),
  ultimoContacto: z.string().nullable(),
  /** null si el cliente no tiene medición de activación. */
  activacion: activationSchema.nullable(),
  diasEnEstado: z.number(),
  /** Ids en Close, Stripe y la plataforma, para los deep links. */
  externalIds: clientExternalIdsSchema,
})
export type Client = z.infer<typeof clientSchema>

/** Conteo por estado, para el bloque de salud del portfolio. */
export const portfolioHealthSchema = z.object({
  porEstado: z.array(
    z.object({
      estado: clientStateSchema,
      cantidad: z.number(),
      pctPortfolio: z.number(),
    }),
  ),
  total: z.number(),
})
export type PortfolioHealth = z.infer<typeof portfolioHealthSchema>

/**
 * Un empleado digital contratado en la plataforma. Es la unidad de la brecha
 * de activación y tiene ficha propia en la plataforma (deep link).
 */
export const agenteSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  clientId: z.string(),
  instalado: z.boolean(),
  /** Id en la plataforma de Laburen. null = no vinculado. */
  plataformaAgentId: z.string().nullable(),
})
export type Agente = z.infer<typeof agenteSchema>

/** Fila del ranking "dinero contratado que no está corriendo". */
export const activationRowSchema = z.object({
  clientId: z.string(),
  clientNombre: z.string(),
  activacion: activationSchema,
  owner: userRefSchema.nullable(),
  externalIds: clientExternalIdsSchema,
})
export type ActivationRow = z.infer<typeof activationRowSchema>
